import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LanguageSelector } from '../components';

const STEPS = ['account', 'personal', 'fitness'];

export default function Register() {
  const { t } = useTranslation();
  const { register, error: authError, isLoading } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    gender: '', age: '', weight: '', height: '',
    goal: '', activityLevel: '', daysPerWeek: '3', timePerSession: '60',
    workoutLocation: 'gym', experienceLevel: 'beginner',
  });
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!form.name.trim()) { setError(t('errors.nameRequired')); return false; }
      if (!form.email.includes('@')) { setError(t('errors.invalidEmail')); return false; }
      if (form.password.length < 8) { setError(t('errors.passwordLength')); return false; }
      if (form.password !== form.confirmPassword) { setError(t('errors.passwordsNoMatch')); return false; }
    }
    if (step === 1) {
      if (!form.gender) { setError('Gender is required'); return false; }
      if (!form.age || Number(form.age) < 16) { setError(t('errors.ageRange')); return false; }
      if (!form.weight || Number(form.weight) < 30) { setError(t('errors.weightRange')); return false; }
      if (!form.height || Number(form.height) < 100) { setError(t('errors.heightRange')); return false; }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setStep(s => s + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;
    const result = await register(form);
    if (!result.success) {
      setError(result.error || 'Registration failed');
    }
  };

  const inputClass = "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";
  const selectClass = inputClass;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-indigo-600 mb-1">Muscle Guide</h1>
          <p className="text-gray-500">{t('register.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">{t('register.title')}</h2>
            <LanguageSelector />
          </div>

          <div className="flex gap-2 mb-6">
            {STEPS.map((s, idx) => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${idx <= step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
            ))}
          </div>
          <p className="text-xs text-gray-500 mb-4">{t('common.step')} {step + 1} {t('common.of')} 3 — {t(`register.step${step + 1}`)}</p>

          {(error || authError) && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">{error || authError}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {step === 0 && <>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.name')}</label>
                <input name="name" value={form.name} onChange={handleChange} className={inputClass} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
                <input name="password" type="password" value={form.password} onChange={handleChange} className={inputClass} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
                <input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} className={inputClass} required /></div>
            </>}

            {step === 1 && <>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.gender')}</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={selectClass}>
                  <option value="">--</option><option value="male">{t('common.male')}</option><option value="female">{t('common.female')}</option>
                </select></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.age')}</label>
                  <input name="age" type="number" value={form.age} onChange={handleChange} className={inputClass} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.weight')} (kg)</label>
                  <input name="weight" type="number" value={form.weight} onChange={handleChange} className={inputClass} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.height')} (cm)</label>
                  <input name="height" type="number" value={form.height} onChange={handleChange} className={inputClass} /></div>
              </div>
            </>}

            {step === 2 && <>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.fitnessGoals')}</label>
                <select name="goal" value={form.goal} onChange={handleChange} className={selectClass}>
                  <option value="">--</option>
                  <option value="muscle_gain">{t('fitnessGoals.muscle_gain')}</option>
                  <option value="weight_loss">{t('fitnessGoals.weight_loss')}</option>
                  <option value="strength">{t('fitnessGoals.strength')}</option>
                  <option value="endurance">{t('fitnessGoals.endurance')}</option>
                  <option value="general_fitness">{t('fitnessGoals.general_fitness')}</option>
                </select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.activityLevel')}</label>
                <select name="activityLevel" value={form.activityLevel} onChange={handleChange} className={selectClass}>
                  <option value="">--</option>
                  <option value="sedentary">{t('activityLevels.sedentary')}</option>
                  <option value="lightly_active">{t('activityLevels.lightly_active')}</option>
                  <option value="moderately_active">{t('activityLevels.moderately_active')}</option>
                  <option value="very_active">{t('activityLevels.very_active')}</option>
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.daysPerWeek')}</label>
                  <select name="daysPerWeek" value={form.daysPerWeek} onChange={handleChange} className={selectClass}>
                    {[1,2,3,4,5,6,7].map(d => <option key={d} value={d}>{d}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.timePerSession')} (min)</label>
                  <input name="timePerSession" type="number" value={form.timePerSession} onChange={handleChange} className={inputClass} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.workoutLocation')}</label>
                  <select name="workoutLocation" value={form.workoutLocation} onChange={handleChange} className={selectClass}>
                    <option value="gym">{t('workoutLocations.gym')}</option><option value="home">{t('workoutLocations.home')}</option>
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">{t('userData.experienceLevel')}</label>
                  <select name="experienceLevel" value={form.experienceLevel} onChange={handleChange} className={selectClass}>
                    <option value="beginner">{t('experienceLevels.beginner')}</option>
                    <option value="intermediate">{t('experienceLevels.intermediate')}</option>
                    <option value="advanced">{t('experienceLevels.advanced')}</option>
                  </select></div>
              </div>
            </>}

            <div className="flex gap-3 pt-2">
              {step > 0 && <button type="button" onClick={() => setStep(s => s - 1)} className="flex-1 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">{t('common.back')}</button>}
              {step < 2 ? (
                <button type="button" onClick={handleNext} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700">{t('common.next')}</button>
              ) : (
                <button type="submit" disabled={isLoading} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">
                  {isLoading ? t('register.submitting') : t('register.createAccount')}
                </button>
              )}
            </div>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-indigo-600 font-medium hover:underline">{t('auth.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
